import React, { useEffect, useRef } from "react";
import { TASK_COLUMNS, STATUS_COLOR, OVERALL_STATUSES, DEV_STATUSES } from "../../lib/constants";
import { PencilSimple, Trash, ArrowUp, ArrowDown, ArrowsDownUp, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function buildTaskShareUrl(task) {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams();
  if (task.project_id) params.set("project_id", task.project_id);
  params.set("focus", task.id);
  if (task.object_id) params.set("search", task.object_id);
  return `${window.location.origin}/?${params.toString()}`;
}

async function copyTaskLink(task) {
  const url = buildTaskShareUrl(task);
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success(`Link to ${task.object_id || "task"} copied`, { description: url });
  } catch {
    toast.error("Could not copy link");
  }
}
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "../ui/select";

function Badge({ value }) {
  if (!value) return <span className="text-neutral-400">—</span>;
  const cls = STATUS_COLOR[value] || "bg-neutral-100 text-neutral-700 border-neutral-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${cls}`}
    >
      {value}
    </span>
  );
}

function ProgressCell({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${
            v >= 100 ? "bg-emerald-600" : v >= 60 ? "bg-sky-600" : "bg-amber-500"
          }`}
          style={{ width: `${v}%` }}
        />
      </div>
      <span className="text-xs font-mono tabular-nums text-neutral-700 w-9 text-right">{v}%</span>
    </div>
  );
}

function formatDate(v, withTime) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    if (withTime) return d.toLocaleString();
    return d.toLocaleDateString();
  } catch {
    return String(v);
  }
}

function InlineStatusEditor({ value, options, onChange, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="h-7 border-0 bg-transparent px-2 py-0 hover:bg-neutral-100 focus:ring-0 shadow-none w-full justify-start"
      >
        {value ? <Badge value={value} /> : <span className="text-neutral-400 text-xs">Set…</span>}
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function TaskTable({
  tasks, visibleColumns, sort, onSort,
  canEdit = true, canDelete = true,
  onEdit, onDelete, onInlineUpdate,
  selectedIds, onToggleRow, onToggleAllOnPage,
  focusTaskId, onFocusHandled,
}) {
  const cols = TASK_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  const sortBy = sort?.by;
  const sortDir = sort?.dir;
  const showSelection = !!selectedIds && !!onToggleRow;
  const selection = selectedIds || new Set();
  const pageIds = tasks.map((t) => t.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selection.has(id));
  const someOnPageSelected = pageIds.some((id) => selection.has(id));
  const focusRowRef = useRef(null);

  // Scroll to & briefly highlight a focused task row (from global search deep link)
  useEffect(() => {
    if (!focusTaskId) return;
    if (!tasks.some((t) => t.id === focusTaskId)) return;
    const el = focusRowRef.current;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => onFocusHandled && onFocusHandled(), 2500);
      return () => clearTimeout(t);
    }
  }, [focusTaskId, tasks, onFocusHandled]);

  const renderSortIcon = (colKey) => {
    if (sortBy !== colKey) {
      return <ArrowsDownUp size={11} weight="bold" className="text-neutral-400 opacity-0 group-hover/th:opacity-100 transition-opacity" />;
    }
    return sortDir === "asc"
      ? <ArrowUp size={11} weight="bold" className="text-[#0A0A0A]" />
      : <ArrowDown size={11} weight="bold" className="text-[#0A0A0A]" />;
  };

  const renderCell = (t, c) => {
    const v = t[c.key];
    if (c.key === "overall_status" && canEdit) {
      return (
        <InlineStatusEditor
          value={v}
          options={OVERALL_STATUSES}
          onChange={(nv) => onInlineUpdate(t.id, { overall_status: nv })}
          testId={`inline-overall-status-${t.id}`}
        />
      );
    }
    if (c.key === "development_status" && canEdit) {
      return (
        <InlineStatusEditor
          value={v}
          options={DEV_STATUSES}
          onChange={(nv) => onInlineUpdate(t.id, { development_status: nv })}
          testId={`inline-dev-status-${t.id}`}
        />
      );
    }
    if (c.type === "badge") return <Badge value={v} />;
    if (c.type === "progress") return <ProgressCell value={v} />;
    if (c.type === "date") return <span className="text-neutral-700 text-sm">{formatDate(v)}</span>;
    if (c.type === "datetime") return <span className="text-neutral-500 text-xs">{formatDate(v, true)}</span>;
    if (v === null || v === undefined || v === "") return <span className="text-neutral-400">—</span>;
    return (
      <span className="text-sm text-neutral-900 line-clamp-2" title={String(v)}>
        {String(v)}
      </span>
    );
  };

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-lg overflow-hidden flex-1 flex flex-col">
      <div className="overflow-auto flex-1" data-testid="task-table-scroll">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              {showSelection ? (
                <th
                  className="sticky top-0 left-0 bg-[#F5F5F5] px-3 py-2 border-b border-r border-[#E5E5E5] z-30"
                  style={{ minWidth: 44, width: 44 }}
                >
                  <input
                    type="checkbox"
                    data-testid="select-all-on-page"
                    aria-label="Select all rows on page"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                    onChange={() => onToggleAllOnPage(pageIds, !allOnPageSelected)}
                    className="cursor-pointer accent-neutral-900"
                  />
                </th>
              ) : null}
              {cols.map((c, idx) => {
                const isActive = sortBy === c.key;
                const stickyLeft = c.sticky
                  ? (idx === 0 ? (showSelection ? 44 : 0) : (showSelection ? 44 : 0) + (cols[0]?.minWidth || 90))
                  : undefined;
                return (
                  <th
                    key={c.key}
                    className={`group/th sticky top-0 bg-[#F5F5F5] px-3 py-2 border-b border-r border-[#E5E5E5] z-10 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600 select-none ${
                      c.sticky ? "sticky-col z-20" : ""
                    } ${isActive ? "bg-neutral-200" : ""}`}
                    style={{
                      minWidth: c.minWidth,
                      left: stickyLeft,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onSort && onSort(c.key)}
                      data-testid={`sort-header-${c.key}`}
                      className="inline-flex items-center gap-1.5 hover:text-[#0A0A0A] cursor-pointer text-left w-full"
                    >
                      <span>{c.label}</span>
                      {renderSortIcon(c.key)}
                    </button>
                  </th>
                );
              })}
              <th
                className="sticky top-0 right-0 bg-[#F5F5F5] px-3 py-2 border-b border-l border-[#E5E5E5] z-20 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600"
                style={{ minWidth: 90 }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td
                  colSpan={cols.length + 1 + (showSelection ? 1 : 0)}
                  className="px-6 py-16 text-center text-neutral-500 text-sm"
                  data-testid="table-empty-state"
                >
                  No tasks match the current filters. Import an Excel file or add your first task.
                </td>
              </tr>
            ) : (
              tasks.map((t) => {
                const isSelected = selection.has(t.id);
                const isFocused = focusTaskId && t.id === focusTaskId;
                return (
                <tr
                  key={t.id}
                  ref={isFocused ? focusRowRef : undefined}
                  data-testid={`task-row-${t.id}`}
                  className={`group border-b border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors ${
                    isSelected ? "bg-sky-50/60 hover:bg-sky-50" : ""
                  } ${isFocused ? "ring-2 ring-inset ring-amber-400 !bg-amber-50" : ""}`}
                >
                  {showSelection ? (
                    <td
                      className={`sticky left-0 px-3 py-2 border-r border-[#E5E5E5] z-10 ${
                        isSelected ? "bg-sky-50/60 group-hover:bg-sky-50" : "bg-white group-hover:bg-[#FAFAFA]"
                      }`}
                      style={{ minWidth: 44, width: 44 }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`select-row-${t.id}`}
                        checked={isSelected}
                        onChange={() => onToggleRow(t.id)}
                        className="cursor-pointer accent-neutral-900"
                        aria-label="Select row"
                      />
                    </td>
                  ) : null}
                  {cols.map((c, idx) => {
                    const stickyLeft = c.sticky
                      ? (idx === 0 ? (showSelection ? 44 : 0) : (showSelection ? 44 : 0) + (cols[0]?.minWidth || 90))
                      : undefined;
                    return (
                    <td
                      key={c.key}
                      className={`px-3 py-2 border-r border-[#E5E5E5] align-top ${
                        c.sticky
                          ? isSelected
                            ? "sticky bg-sky-50/60 group-hover:bg-sky-50 z-10"
                            : "sticky bg-white group-hover:bg-[#FAFAFA] z-10"
                          : ""
                      }`}
                      style={{
                        minWidth: c.minWidth,
                        maxWidth: 320,
                        left: stickyLeft,
                      }}
                    >
                      {renderCell(t, c)}
                    </td>
                    );
                  })}
                  <td className={`sticky right-0 px-3 py-2 border-l border-[#E5E5E5] z-10 ${
                    isSelected ? "bg-sky-50/60 group-hover:bg-sky-50" : "bg-white group-hover:bg-[#FAFAFA]"
                  }`}>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyTaskLink(t)}
                        data-testid={`copy-link-btn-${t.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-neutral-200 text-neutral-500 hover:text-neutral-900 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        title="Copy shareable link"
                      >
                        <LinkSimple size={14} weight="bold" />
                      </button>
                      {canEdit ? (
                        <button
                          onClick={() => onEdit(t)}
                          data-testid={`edit-task-btn-${t.id}`}
                          className="h-7 w-7 flex items-center justify-center rounded hover:bg-neutral-200 text-neutral-700"
                          title="Edit"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          onClick={() => onDelete(t)}
                          data-testid={`delete-task-btn-${t.id}`}
                          className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      ) : null}
                      {!canEdit && !canDelete ? (
                        <span className="text-[10px] text-neutral-400 uppercase tracking-wider">Read only</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
